import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { PartnersService } from "../../services/partners.service";
import { PartnersDataType } from "../../interfaces/admin.interface";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";

const Partners = () => {
  const [partners, setPartners] = useState<PartnersDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<PartnersDataType | null>(
    null
  );
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    PartnersService.getAllPartners()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: PartnersDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setPartners(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await PartnersService.getAllPartners();
    setPartners(
      updatedData?.data?.data?.map((item: PartnersDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      logo: null,
      is_visible: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Partnyor adı tələb olunur"),
      logo: Yup.string().required("Partnyor logosu tələb olunur"),
    }),
    onSubmit: async (values) => {
      if (isEditing && editingRecord) {
        const id = editingRecord.id;
        const isEqual =
          editingRecord.name === values.name &&
          editingRecord.logo === values.logo &&
          editingRecord.is_visible === values.is_visible;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          if (editingRecord.logo === values.logo) {
            const payload = {
              name: values.name,
              is_visible: values.is_visible,
            };
            const response = await PartnersService.updatePartner({
              ...payload,
              id,
            });
            if (response.status === 200) {
              message.success("Partnyor uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Partnyoru yeniləmək alınmadı."
              );
            }
          } else {
            const response = await PartnersService.updatePartner({
              ...values,
              id,
            });
            if (response.status === 200) {
              message.success("Partnyor uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Partnyoru yeniləmək alınmadı."
              );
            }
          }
        }
      } else {
        const response = await PartnersService.addPartner(values);
        if (response.status === 201) {
          message.success("Partnyor uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(
            response.errorMessage || "Partnyor əlavə etmək alınmadı."
          );
        }
      }
      setLoading(false);
    },
  });

  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    PartnersService.updatePartner({
      id: recordId,
      is_visible: checked,
    });
    if (checked) {
      message.success("Partnyor artıq saytda görünür.");
    } else {
      message.info("Partnyor artıq saytda görünmür.");
    }
    setPartners((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, is_visible: checked } : item
      )
    );
  };

  const handleDeleteClick = async (record: PartnersDataType) => {
    try {
      await PartnersService.deletePartner(record.id.toString());
      setPartners((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Partnyor uğurla silindi!");
    } catch (error) {
      message.error("Partnyoru silmək alınmadı.");
    }
  };
  const columns: TableProps<PartnersDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Ad",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Logo",
      dataIndex: "logo",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.logo!);
            setShowImageModal(true);
          }}
        >
          {record.logo ? (
            <img
              style={{ width: "100px" }}
              src={record.logo}
              alt={`${record.name} logo`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Saytda görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu partnyoru siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setFieldValue("logo", null);
    setLogoFileList([]);
  };
  const handleEditClick = (record: PartnersDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("logo", record.logo);
    setFieldValue("is_visible", record.is_visible);
    setLogoFileList(
      record.logo
        ? [
            {
              uid: "-1",
              name: `${record.name} logo`,
              status: "done",
              url: record.logo,
            },
          ]
        : []
    );
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Partnyorlar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Partnyorlar</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<PartnersDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={partners}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Partnyoru redaktə edin" : "Yeni Partnyor əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Partnyor adı
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Partnyor adı"
                value={values.name}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="logo"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage error={errors.logo} touched={touched.logo} />
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
              Partnyorun saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>
          <div>
            <MainButton full type="submit">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Partners;
